import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { money } from '../../lib/money';
import { useNavigate } from 'react-router-dom';
import {
  Badge,
  Button,
  DataTable,
  EmptyState,
  FilterBar,
  PageContainer,
  PageHeader,
  Pagination,
  Spinner,
} from '../../ui/components';
import { Icon } from '../../ui/icons';
import { useProperty } from '../../app/PropertyContext';
import { useT } from '../../i18n';
import { stockCountPath } from '../../lib/slug';
import { buildFilterParams } from '../../lib/filters';
import { catalogApi } from '../catalog/catalogApi';
import { countsApi, countStatusMeta, countTypeLabel, COUNT_STATUSES, COUNT_TYPES } from './countsApi';
import '../requisitions/requisitions.css';

const PAGE_SIZE = 25;
export default function CountsPage() {
  const navigate = useNavigate();
  const t = useT();
  const { activePropertyId, hasPermission } = useProperty();
  const canCount = hasPermission('inventory.count');

  const [data, setData] = useState({ items: [], totalCount: 0, page: 1, pageSize: PAGE_SIZE });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchText, setSearchText] = useState('');
  const [search, setSearch] = useState('');
  const [values, setValues] = useState({});
  const [locations, setLocations] = useState([]);
  const [page, setPage] = useState(1);
  const debounceRef = useRef(null);

  useEffect(() => {
    let active = true;
    catalogApi.listLocations(activePropertyId).then((res) => { if (active) setLocations(res || []); }).catch(() => {});
    return () => { active = false; };
  }, [activePropertyId]);

  const fields = useMemo(() => [
    { id: 'statuses', label: t('Status'), type: 'multiselect', options: COUNT_STATUSES.map((s) => ({ value: s.value, label: t(s.label) })) },
    { id: 'countTypes', label: t('Type'), type: 'multiselect', options: COUNT_TYPES.map((c) => ({ ...c, label: t(c.label) })) },
    { id: 'locationIds', label: t('Location'), type: 'multiselect', searchable: true, options: locations.map((l) => ({ value: l.id, label: l.name })) },
    { id: 'counted', label: t('Count date'), type: 'daterange', fromParam: 'countedFrom', toParam: 'countedTo' },
  ], [locations, t]);

  const filterParams = useMemo(() => buildFilterParams(fields, values), [fields, values]);
  const hasFilters = search !== '' || Object.keys(filterParams).length > 0;

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await countsApi.list({ page, pageSize: PAGE_SIZE, search, ...filterParams });
      setData(res);
    } catch (err) {
      setError(err?.message || t('Failed to load counts.'));
    } finally {
      setLoading(false);
    }
  }, [page, search, filterParams, t]);

  useEffect(() => { load(); }, [load]);

  const onSearchChange = (value) => {
    setSearchText(value);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => { setPage(1); setSearch(value); }, 300);
  };

  const onFilterChange = (id, value) => {
    setPage(1);
    setValues((prev) => {
      const next = { ...prev };
      if (value === undefined) delete next[id];
      else next[id] = value;
      return next;
    });
  };

  const locationName = (lid) => locations.find((l) => l.id === lid)?.name;
  const open = (row) => navigate(stockCountPath(row));

  const columns = [
    { key: 'number', header: t('Count'), width: 130, render: (c) => <span style={{ fontFamily: 'var(--font-mono)' }}>{c.number || t('Draft')}</span> },
    { key: 'location', header: t('Location'), width: 180, render: (c) => locationName(c.locationId) || <span className="pp-cell-muted">—</span> },
    { key: 'type', header: t('Type'), width: 120, render: (c) => t(countTypeLabel(c.countType)) },
    {
      key: 'status', header: t('Status'), width: 110,
      render: (c) => {
        const m = countStatusMeta(c.status);
        return <Badge tone={m.tone} dot>{t(m.label)}</Badge>;
      },
    },
    { key: 'date', header: t('Count date'), width: 120, render: (c) => (c.countDate ? new Date(c.countDate).toLocaleDateString() : '—') },
    { key: 'lines', header: t('Counted'), width: 100, align: 'right', render: (c) => `${c.countedCount}/${c.lineCount}` },
    {
      key: 'variance', header: t('Variance'), width: 140, align: 'right',
      render: (c) => {
        if (!c.varianceValue) return <span className="pp-cell-muted">—</span>;
        const amt = Number(c.varianceValue.amount);
        const tone = amt < 0 ? 'var(--color-danger-600, #b42318)' : amt > 0 ? 'var(--color-success-600, #067647)' : 'inherit';
        return <span style={{ color: tone, fontWeight: 600 }}>{money(c.varianceValue)}</span>;
      },
    },
  ];

  const totalPages = Math.max(1, Math.ceil(data.totalCount / PAGE_SIZE));

  return (
    <PageContainer size="full">
      <PageHeader
        title={t('Stock Counts')}
        description={t("Physical and cycle counts. Open a count sheet for a location, record what's physically on hand, then post the variances to the inventory ledger.")}
        icon={<Icon.ClipboardList size={22} />}
        actions={canCount ? (
          <Button variant="primary" leadingIcon={<Icon.Plus size={16} />} onClick={() => navigate('/counts/new')}>
            {t('New count')}
          </Button>
        ) : null}
      />

      <FilterBar
        searchPlaceholder={t('Search by count number or notes…')}
        searchValue={searchText}
        onSearchChange={onSearchChange}
        fields={fields}
        values={values}
        onChange={onFilterChange}
      />

      {error ? (
        <EmptyState
          icon={<Icon.CircleAlert size={28} />}
          title={t("Couldn't load counts")}
          description={error}
          action={<Button variant="secondary" onClick={load}>{t('Retry')}</Button>}
        />
      ) : loading ? (
        <div style={{ display: 'grid', placeItems: 'center', padding: 'var(--space-16)' }}><Spinner size={26} /></div>
      ) : (
        <>
          <DataTable
            columns={columns}
            data={data.items}
            rowKey={(c) => c.id}
            density="comfortable"
            onRowClick={open}
            emptyState={
              <EmptyState
                icon={<Icon.ClipboardList size={28} />}
                title={hasFilters ? t('No matching counts') : t('No stock counts yet')}
                description={hasFilters ? t('Try adjusting your search or filters.') : t('Start a count to reconcile physical stock against the system on-hand balances.')}
                action={canCount && !hasFilters ? <Button variant="secondary" onClick={() => navigate('/counts/new')}>{t('New count')}</Button> : null}
              />
            }
          />
          {data.totalCount > 0 && (
            <div style={{ marginTop: 'var(--space-4)' }}>
              <Pagination page={page} pageCount={totalPages} onPageChange={setPage} totalItems={data.totalCount} pageSize={PAGE_SIZE} />
            </div>
          )}
        </>
      )}
    </PageContainer>
  );
}
